
import React from 'react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Clock, AlertTriangle, Apple, Coffee, Check } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useFasting } from '@/contexts/FastingContext';
import { Separator } from '@/components/ui/separator';

export const RefeedingPage: React.FC = () => {
  const { selectedProtocol } = useFasting();
  const isExtendedFast = selectedProtocol.durationHours >= 48;
  
  const getRefeedingTips = () => {
    if (selectedProtocol.durationHours <= 24) {
      return [
        {
          title: "Start Small",
          description: "Begin with a small meal that's easily digestible. Avoid large quantities initially.",
          icon: <Apple size={18} />
        },
        {
          title: "Wait Before Second Meal",
          description: "Wait 1-2 hours before having a second small meal if you're still hungry.",
          icon: <Clock size={18} />
        },
        {
          title: "Focus on Whole Foods",
          description: "Choose nutrient-dense whole foods like fruits, vegetables, lean proteins and healthy fats.",
          icon: <Check size={18} />
        },
        {
          title: "Stay Hydrated",
          description: "Continue drinking water throughout your eating window.",
          icon: <Coffee size={18} />
        },
        {
          title: "Avoid Sugar and Processed Foods",
          description: "These can spike your insulin and make you feel unwell after a fast.",
          icon: <AlertTriangle size={18} />
        }
      ];
    } else {
      return [
        {
          title: "Start with Bone Broth",
          description: "Begin with 1-2 cups of bone broth to gently reintroduce nutrients.",
          icon: <Coffee size={18} />
        },
        {
          title: "Wait One Hour",
          description: "Wait at least one hour before introducing solid foods.",
          icon: <Clock size={18} />
        },
        {
          title: "Begin with Soft Foods",
          description: "Start with small portions of soft, cooked vegetables, soups, or easily digestible proteins.",
          icon: <Apple size={18} />
        },
        {
          title: "Gradually Reintroduce Foods",
          description: "Over 2-3 days, gradually increase portion sizes and add more variety to your diet.",
          icon: <Check size={18} />
        },
        {
          title: "Watch for Refeeding Symptoms",
          description: "Monitor for digestive discomfort, bloating, or fatigue. If these occur, slow down refeeding.",
          icon: <AlertTriangle size={18} />
        },
        {
          title: "Avoid Heavy Meals",
          description: "Avoid large, heavy meals for at least 48-72 hours post-fast.",
          icon: <AlertTriangle size={18} />
        }
      ];
    }
  };

  const tips = getRefeedingTips();
  
  return (
    <div className="pb-12">
      <div className="mb-8 flex items-center">
        <Button variant="ghost" className="mr-2" asChild>
          <Link to="/timer">
            <ArrowLeft size={18} />
          </Link>
        </Button>
        <h1 className="text-2xl font-bold text-fasttrack-blue">Refeeding Guide</h1>
      </div>
      
      <Card className="mb-6 p-5 bg-gradient-to-r from-fasttrack-light-blue/30 to-fasttrack-light-blue/10">
        <h2 className="text-xl font-semibold text-fasttrack-blue mb-2">
          {isExtendedFast ? "Extended Fast Refeeding" : "Breaking Your Fast"} 
        </h2>
        
        <p className="text-muted-foreground mb-4">
          {isExtendedFast 
            ? "After an extended fast, proper refeeding is crucial to avoid digestive issues and refeeding syndrome. Follow these guidelines:"
            : "Breaking your fast properly ensures you maintain the benefits and avoid digestive discomfort. Follow these tips:"}
        </p>
        
        {isExtendedFast && (
          <Badge variant="destructive" className="mb-2">Important</Badge>
        )}
      </Card>
      
      {tips.map((tip, index) => (
        <React.Fragment key={index}>
          <div className="flex gap-4 p-4">
            <div className="h-10 w-10 rounded-full bg-fasttrack-blue/10 flex items-center justify-center flex-shrink-0">
              <div className="text-fasttrack-blue">
                {tip.icon}
              </div>
            </div>
            <div>
              <h3 className="font-medium text-foreground">{tip.title}</h3>
              <p className="text-sm text-muted-foreground">{tip.description}</p>
            </div>
          </div>
          {index < tips.length - 1 && <Separator />}
        </React.Fragment>
      ))}
      
      <Card className="mt-8 p-4 bg-blue-50 border-blue-200">
        <h3 className="font-semibold text-fasttrack-blue mb-2">Foods to Consider</h3>
        <div className="grid grid-cols-2 gap-2 text-sm">
          <div>
            <Badge variant="outline" className="bg-green-100 mb-2">Recommended</Badge>
            <ul className="list-disc list-inside space-y-1 text-green-700">
              <li>Bone broth</li>
              <li>Fermented foods</li>
              <li>Cooked vegetables</li>
              <li>Light proteins</li>
              <li>Healthy fats</li>
            </ul>
          </div>
          <div>
            <Badge variant="outline" className="bg-red-100 mb-2">Avoid Initially</Badge>
            <ul className="list-disc list-inside space-y-1 text-red-700">
              <li>Processed foods</li>
              <li>Sugary foods</li>
              <li>Heavy meals</li>
              <li>Alcohol</li>
              <li>Dairy (for some people)</li>
            </ul>
          </div>
        </div>
      </Card>
      
      <div className="text-center text-xs text-muted-foreground mt-8">
        Designed by wyattsanders.com
      </div>
    </div>
  );
};
